<template>
  <!-- A stable root element: the keyed async Canvas remounts on param
       navigation, and a real root keeps the route <Suspense> anchor valid
       (see .stable-route-root in global.scss) and gives the RouterView's
       fallthrough attrs (e.g. the @open-task listener) somewhere to land. -->
  <div class="stable-route-root">
    <Suspense>
      <template #default>
        <!-- When blocked by entitlement, we open the upgrade modal and avoid rendering the canvas. -->
        <div v-if="blockedByEntitlement" class="canvas-blocked" />
        <Canvas
          v-else
          :key="`${workspaceId}:${rowId}`"
          :organization-id="orgId"
          :workspace-id="workspaceId"
          :row-id="rowId"
          :start-in-edit-mode="startInEditMode"
        />
      </template>
      <template #fallback>
        <ObjectLoader />
      </template>
    </Suspense>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";

import Canvas from "@/components/canvas/Canvas.vue";
import * as inject from "@/components/inject";
import ObjectLoader from "@/components/ObjectLoader.vue";
import { workspaceBlockedByEntitlement } from "@/components/workspaces";

export default defineComponent({
  name: "CanvasRoute",
  components: { Canvas, ObjectLoader },
  props: {
    orgId: { type: String, required: true },
    workspaceId: { type: String, required: true },
    rowId: { type: String, required: true },
  },
  async setup(props) {
    const controller = inject.controller();
    const profile = inject.profile();
    const billing = inject.billing();
    const router = inject.router();
    const logger = inject.logger("CanvasRoute");

    // `?edit=1` opens the canvas directly in edit mode (set by the Studio
    // screen right after creating a canvas). Computed, not captured: the
    // route component survives param-only navigations (the Canvas child is
    // keyed per workspace/row and re-reads the prop when it remounts).
    const startInEditMode = computed(
      () => router.currentRoute.value.query.edit === "1"
    );

    // Workspaces of custom organizations require the "workspaces"
    // entitlement; open the upgrade modal instead of the canvas when blocked.
    const blockedByEntitlement = await workspaceBlockedByEntitlement({
      controller,
      profile: profile.value,
      billing,
      workspaceId: props.workspaceId,
      logger,
    });

    return { blockedByEntitlement, startInEditMode };
  },
});
</script>
